import React, { useEffect, useRef, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_JS_LOADER_OPTIONS } from "../../config/googleMapsJsLoaderOptions";
import { X, MinusCircle } from "lucide-react";
import { CustomSelect } from "../CustomSelection";

const MONTH_OPTS = [
  { value: "", label: "Month" },
  { value: "1", label: "01-Jan" },
  { value: "2", label: "02-Feb" },
  { value: "3", label: "03-Mar" },
  { value: "4", label: "04-Apr" },
  { value: "5", label: "05-May" },
  { value: "6", label: "06-Jun" },
  { value: "7", label: "07-Jul" },
  { value: "8", label: "08-Aug" },
  { value: "9", label: "09-Sep" },
  { value: "10", label: "10-Oct" },
  { value: "11", label: "11-Nov" },
  { value: "12", label: "12-Dec" },
];

const NIGERIA_STATE_NAMES = [
  "Abia",
  "Adamawa",
  "Akwa Ibom",
  "Anambra",
  "Bauchi",
  "Bayelsa",
  "Benue",
  "Borno",
  "Cross River",
  "Delta",
  "Ebonyi",
  "Edo",
  "Ekiti",
  "Enugu",
  "FCT",
  "Gombe",
  "Imo",
  "Jigawa",
  "Kaduna",
  "Kano",
  "Katsina",
  "Kebbi",
  "Kogi",
  "Kwara",
  "Lagos",
  "Nasarawa",
  "Niger",
  "Ogun",
  "Ondo",
  "Osun",
  "Oyo",
  "Plateau",
  "Rivers",
  "Sokoto",
  "Taraba",
  "Yobe",
  "Zamfara",
];

const NIGERIA_STATES = NIGERIA_STATE_NAMES.map((s) => ({
  value: s,
  label: s,
}));

const COUNTRIES = [{ value: "Nigeria", label: "Nigeria" }];

const buildYearOpts = () => {
  const y = new Date().getFullYear();
  const out = [{ value: "", label: "Year" }];
  for (let yr = y; yr >= y - 80; yr -= 1) {
    out.push({ value: String(yr), label: String(yr) });
  }
  return out;
};

const YEAR_OPTS = buildYearOpts();

const emptyForm = () => {
  return {
    street: "",
    city: "",
    country: "Nigeria",
    postcode: "",
    state: "Lagos",
    moveInMonth: "",
    moveInYear: "",
    addReference: "no",
  };
};

const pickComponent = (components, ...typeOptions) => {
  for (const types of typeOptions) {
    const arr = Array.isArray(types) ? types : [types];
    const found = components.find((c) => arr.some((t) => c.types.includes(t)));
    if (found) return found.long_name;
  }
  return "";
};

const matchNigeriaState = (raw) => {
  if (!raw) return "Lagos";
  let r = raw.replace(/\s+State$/i, "").trim();
  const rl = r.toLowerCase();
  if (
    rl.includes("federal capital") ||
    rl.includes("abuja") ||
    r === "Federal Capital Territory"
  ) {
    return "FCT";
  }
  const exact = NIGERIA_STATE_NAMES.find((s) => s.toLowerCase() === rl);
  if (exact) return exact;
  const partial = NIGERIA_STATE_NAMES.find(
    (s) => rl.includes(s.toLowerCase()) || s.toLowerCase().includes(rl)
  );
  if (partial) return partial;
  return "Lagos";
};

const parsePlaceComponents = (components) => {
  const streetNumber = pickComponent(components, "street_number");
  const route = pickComponent(components, "route");
  const street = [streetNumber, route].filter(Boolean).join(" ").trim();

  let city =
    pickComponent(components, "locality") ||
    pickComponent(components, "postal_town") ||
    pickComponent(components, ["sublocality", "sublocality_level_1"]) ||
    pickComponent(components, "administrative_area_level_2");

  const stateRaw = pickComponent(components, "administrative_area_level_1");
  const postcode = pickComponent(components, "postal_code");
  const countryLong = pickComponent(components, "country");

  return {
    street: street || pickComponent(components, "premise") || "",
    city,
    postcode,
    state: matchNigeriaState(stateRaw),
    country: countryLong === "Nigeria" ? "Nigeria" : "Nigeria",
  };
};

const inputClass =
  "w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#1A1A1A] outline-none transition-shadow placeholder:text-[#9CA3AF] focus:border-[#097FE6] focus:ring-2 focus:ring-[#097FE6]/20";

const CurrentAddressModal = ({
  open,
  onClose,
  existingAddress,
  onSave,
  onDelete,
}) => {
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_JS_LOADER_OPTIONS);

  const [form, setForm] = useState(emptyForm);
  const streetInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const isEditing = Boolean(existingAddress);

  useEffect(() => {
    const el = document.createElement("style");
    el.setAttribute("data-current-address-modal-pac", "1");
    el.textContent =
      ".pac-container { z-index: 2147483646 !important; pointer-events: auto; }";
    document.head.appendChild(el);
    return () => {
      document.head.removeChild(el);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    if (existingAddress) {
      const st = existingAddress.state;
      const stateOk = NIGERIA_STATE_NAMES.includes(st);
      setForm({
        ...emptyForm(),
        ...existingAddress,
        country: "Nigeria",
        state: stateOk ? st : matchNigeriaState(st),
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, existingAddress]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !isLoaded || loadError) return;
    const g = window.google;
    if (!g?.maps?.places) return;
    const input = streetInputRef.current;
    if (!input) return;

    const ac = new g.maps.places.Autocomplete(input, {
      componentRestrictions: { country: "ng" },
      fields: ["address_components", "formatted_address"],
      types: ["address"],
    });

    autocompleteRef.current = ac;

    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place?.address_components?.length) return;
      const parsed = parsePlaceComponents(place.address_components);
      setForm((f) => ({
        ...f,
        ...parsed,
        street: parsed.street || place.formatted_address || f.street,
      }));
    });

    return () => {
      if (window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(ac);
      }
      autocompleteRef.current = null;
    };
  }, [isLoaded, loadError, open]);

  const set = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  const refBtn =
    "rounded-lg border px-4 py-2 text-sm font-medium transition-colors";

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="current-address-modal-title"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[#E5E7EB] px-5 pb-4 pt-5 sm:px-6">
          <h2
            id="current-address-modal-title"
            className="text-lg font-semibold text-[#000929]"
          >
            Current address
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-[#6B7280] transition-colors hover:bg-[#F3F4F6] hover:text-[#333333]"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-5 sm:px-6">
          <div className="space-y-5">
            <div>
              <label
                htmlFor="ca-street"
                className="mb-1.5 block text-sm font-semibold text-[#000929]"
              >
                Street and number
              </label>
              <input
                ref={streetInputRef}
                id="ca-street"
                type="text"
                value={form.street}
                onChange={(e) => set("street")(e.target.value)}
                className={inputClass}
                autoComplete="off"
                placeholder={
                  isLoaded
                    ? "Start typing a Nigerian address…"
                    : "Loading address search…"
                }
                disabled={!isLoaded && !loadError}
              />
              {loadError ? (
                <p className="mt-1.5 text-xs text-red-600">
                  Address search could not load. Check your Google API key and
                  Places API.
                </p>
              ) : (
                <p className="mt-1.5 text-xs leading-relaxed text-[#6B7280]">
                  Suggestions are limited to Nigeria. Pick a result to fill
                  city, postcode, and state.
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="ca-city"
                className="mb-1.5 block text-sm font-semibold text-[#000929]"
              >
                City or suburb
              </label>
              <input
                id="ca-city"
                type="text"
                value={form.city}
                onChange={(e) => set("city")(e.target.value)}
                className={inputClass}
                autoComplete="off"
              />
            </div>

            <div>
              <label
                htmlFor="ca-country"
                className="mb-1.5 block text-sm font-semibold text-[#000929]"
              >
                Country
              </label>
              <CustomSelect
                id="ca-country"
                value={form.country}
                onChange={set("country")}
                options={COUNTRIES}
                placeholder="Country"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="ca-postcode"
                  className="mb-1.5 block text-sm font-semibold text-[#000929]"
                >
                  Postcode
                </label>
                <input
                  id="ca-postcode"
                  type="text"
                  value={form.postcode}
                  onChange={(e) => set("postcode")(e.target.value)}
                  className={inputClass}
                  autoComplete="off"
                />
              </div>
              <div>
                <label
                  htmlFor="ca-state"
                  className="mb-1.5 block text-sm font-semibold text-[#000929]"
                >
                  State
                </label>
                <CustomSelect
                  id="ca-state"
                  value={form.state}
                  onChange={set("state")}
                  options={NIGERIA_STATES}
                  placeholder="State"
                />
              </div>
            </div>

            <div>
              <span className="mb-1.5 block text-sm font-semibold text-[#000929]">
                When did you move in?
              </span>
              <div className="grid grid-cols-2 gap-3">
                <CustomSelect
                  id="ca-move-month"
                  value={form.moveInMonth}
                  onChange={set("moveInMonth")}
                  options={MONTH_OPTS}
                  placeholder="Month"
                />
                <CustomSelect
                  id="ca-move-year"
                  value={form.moveInYear}
                  onChange={set("moveInYear")}
                  options={YEAR_OPTS}
                  placeholder="Year"
                />
              </div>
            </div>

            <div>
              <span className="mb-1.5 block text-sm font-semibold text-[#000929]">
                Would you like to add a reference?
              </span>
              <p className="mb-3 text-xs leading-relaxed text-[#6B7280]">
                Providing a reference can help show your ability to care for a
                property.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => set("addReference")("yes")}
                  className={`${refBtn} flex-1 ${form.addReference === "yes"
                    ? "border-[#097FE6] bg-[#097FE6] text-white"
                    : "border-[#E5E7EB] bg-white text-[#000929] hover:bg-[#F9FAFB]"
                    }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => set("addReference")("no")}
                  className={`${refBtn} flex-1 ${form.addReference === "no"
                    ? "border-[#097FE6] bg-[#097FE6] text-white"
                    : "border-[#E5E7EB] bg-white text-[#000929] hover:bg-[#F9FAFB]"
                    }`}
                >
                  No
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-[1fr_auto_1fr] items-center gap-2 border-t border-[#E5E7EB] pt-5">
            <button
              type="button"
              onClick={onClose}
              className="justify-self-start text-sm font-medium text-[#374151] hover:underline"
            >
              Cancel
            </button>
            <div className="flex justify-center">
              {isEditing ? (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="flex items-center gap-2 text-sm font-medium text-[#C2410C] hover:underline"
                >
                  <MinusCircle className="h-5 w-5 shrink-0" strokeWidth={2} />
                  Delete address
                </button>
              ) : null}
            </div>
            <button
              type="submit"
              className="justify-self-end rounded-lg bg-[#097FE6] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0868c4]"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CurrentAddressModal;
